/* eslint-disable max-lines */
import { useAIRecommendation } from '@hakimo-ui/hakimo/data-access';
import { UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { StarIcon } from '@hakimo-ui/hakimo/ui-elements';
import { useUser } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { CircleAlert, CircleCheckBig, Globe, Sparkles } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import ClickableTimestamp from '../sop-workflow/ai-recommendation/ClickableTimestamp';
import LoiteringDetails from '../sop-workflow/ai-recommendation/LoiteringRecommendation';
import {
  createTimestampClickHandler,
  REFETCH_INTERVAL,
  RESOLVE_RECOMMENDATION,
  useAlarmGroupContext,
} from '../sop-workflow/ai-recommendation/utils';
import { useAlarmGroupStatic } from '../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { formatEnterpriseAlarmSummary } from '../unified-alarm-group/utils';
import { CARD_VARIANTS } from './sop-card-utils';

interface Props {
  alarmId: string;
  product: UnifiedMonitoringProduct;
  timeZone: string;
  hideDetails?: boolean;
  /** Reveals the collapse chevron and starts collapsed; headline still shown. */
  collapsible?: boolean;
  children?: ReactNode;
}

const ACCENTS = {
  escalate: {
    strip: 'border-l-alert-500',
    icon: 'text-alert-500',
    pill: 'bg-alert-50 text-verdict-escalate dark:bg-alert-500/15 dark:text-alert-400',
    Headline: CircleAlert,
  },
  resolve: {
    strip: 'border-l-status-emerald',
    icon: 'text-status-emerald',
    pill: 'bg-status-emerald/15 text-verdict-resolve dark:text-status-emerald',
    Headline: CircleCheckBig,
  },
};

export function AIRecommendationCard({
  alarmId,
  product,
  timeZone,
  hideDetails = false,
  collapsible = false,
  children,
}: Props) {
  const { data } = useAIRecommendation(alarmId, product, REFETCH_INTERVAL);
  const aiOperatorRep = data ? data[0] : undefined;
  const isLoitering = aiOperatorRep?.rawState?.type === 'loitering';
  const loiteringData = aiOperatorRep?.rawState?.cameras;
  const user = useUser();
  const { enterpriseAlarmDetails } = useAlarmGroupStatic();

  const alarmGroupContext = useAlarmGroupContext();
  const handleTimestampClick = createTimestampClickHandler(
    alarmGroupContext,
    user
  );

  const [collapsedByUser, setCollapsedByUser] = useState(collapsible);
  useEffect(() => setCollapsedByUser(collapsible), [collapsible]);

  const enterpriseSummary =
    !aiOperatorRep?.recommendations && enterpriseAlarmDetails
      ? formatEnterpriseAlarmSummary(enterpriseAlarmDetails, timeZone || 'UTC')
      : undefined;

  if (!aiOperatorRep?.recommendations && !enterpriseSummary) {
    return children;
  }

  const headline = aiOperatorRep?.headline ?? enterpriseAlarmDetails?.alarmType;
  const summary = aiOperatorRep?.summary ?? enterpriseSummary;
  const isResolve =
    !hideDetails && aiOperatorRep?.recommendations === RESOLVE_RECOMMENDATION;
  const accent = isResolve ? ACCENTS.resolve : ACCENTS.escalate;
  const HeadlineIcon = accent.Headline;
  const collapsed = collapsedByUser && collapsible;

  return (
    <div
      className={clsx(
        'dark:bg-hakimo-dark-bg bg-onlight-bg-3 space-y-3 rounded-lg border-l-2 px-3 py-4 text-sm',
        accent.strip
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Sparkles className={clsx('h-4 w-4 shrink-0', accent.icon)} />
          <span className="text-onlight-text-1 dark:text-ondark-text-1 whitespace-nowrap font-bold">
            Hakimo AI
          </span>
        </div>
        <span
          className={clsx(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 font-semibold',
            accent.pill
          )}
        >
          <StarIcon className="h-3 w-3 fill-current" />
          {isResolve ? 'Resolve' : 'Escalate'} Recommended
        </span>
      </div>

      {!hideDetails && (headline || collapsible) && (
        <div className="flex items-center gap-2">
          {headline && (
            <>
              <HeadlineIcon className={clsx('h-5 w-5 shrink-0', accent.icon)} />
              <h4
                className={clsx(
                  'text-base font-bold leading-tight',
                  accent.icon
                )}
              >
                {headline}
              </h4>
            </>
          )}
          {collapsible && (
            <Button
              variant="icon"
              onClick={() => setCollapsedByUser((prev) => !prev)}
              className={clsx(
                'text-onlight-text-2 hover:text-onlight-text-1 ml-auto shrink-0 !p-1 dark:text-white dark:hover:text-white',
                CARD_VARIANTS.shell.iconHover
              )}
              title={
                collapsed ? 'Expand recommendation' : 'Collapse recommendation'
              }
            >
              {collapsed ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronUpIcon className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      )}

      {!collapsed && !hideDetails && !isLoitering && summary && (
        <div className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-onlight-line-3 dark:scrollbar-thumb-ondark-line-3 max-h-36 overflow-y-auto pr-2">
          <p className="text-onlight-text-2 dark:text-ondark-text-2 leading-relaxed">
            <ClickableTimestamp
              text={summary}
              onTimestampClick={handleTimestampClick}
            />
          </p>
        </div>
      )}

      {!collapsed && !hideDetails && isLoitering && !!loiteringData?.length && (
        <LoiteringDetails
          cameras={loiteringData}
          onTimestampClick={handleTimestampClick}
          timeZone={timeZone}
        />
      )}

      {children}

      {!collapsed && !hideDetails && (
        <div className="text-onlight-text-3 dark:text-ondark-text-3 flex items-center gap-1.5">
          <Globe className="h-4 w-4 shrink-0" />
          <span className="text-xs font-medium uppercase tracking-wide">
            {timeZone}
          </span>
        </div>
      )}
    </div>
  );
}

export default AIRecommendationCard;
